/**
 * ARIA Apollo Client Configuration
 * Version: 1.0.0
 */

import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { store, logout } from '../store';

// ARIA auth Option B (see AUTH_ENTRA_ID_SSO_PLAN.md) — username/password only,
// stateless JWT stored in localStorage. Single source of truth for the token
// so the login page, the auth guard, and this client all agree on where it lives.
const ACCESS_TOKEN_KEY = 'accessToken';

export function getAuthToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

const API_URL = import.meta.env.VITE_API_URL || '/graphql';

// Base origin of the backend (strips the trailing /graphql), used to build
// non-GraphQL backend links like the SAML SSO login route.
export const API_BASE_URL = API_URL.replace(/\/graphql\/?$/, '');

const httpLink = createHttpLink({
  uri: API_URL,
});

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Shared with individual components' own catch blocks (e.g. AddJobModal,
// Warranty) so they can skip showing their own generic error when the real
// cause is an expired session — the redirect and message below already cover
// that case, and a second, unrelated "please try again" alert on top of it
// is actively misleading (retrying with the same expired token fails the
// same way every time).
export function isUnauthenticatedError(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors;
  return !!graphQLErrors?.some((err) => err.extensions?.code === 'UNAUTHENTICATED');
}

// Any query/mutation rejected by the backend's auth gate (expired/invalid
// token) drops the stale token and flips Redux back to logged-out — the
// RequireAuth route guard then redirects to /login on its next render.
const errorLink = onError(({ graphQLErrors }) => {
  const isUnauthenticated = graphQLErrors?.some(
    (err) => err.extensions?.code === 'UNAUTHENTICATED',
  );
  if (isUnauthenticated && getAuthToken()) {
    clearAuthToken();
    store.dispatch(logout('Your session has expired. Please log in again.'));
  }
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          jobs: {
            keyArgs: ['status', 'jobType', 'technicianId', 'search'],
            merge(existing, incoming, { args }) {
              if (!args?.page || args.page === 1) {
                return incoming;
              }
              return {
                ...incoming,
                items: [...(existing?.items || []), ...incoming.items],
              };
            },
          },
        },
      },
      Job: {
        fields: {
          scheduling: { merge: true },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
